import { computed, Injectable, signal } from '@angular/core';
import type { MockActiveState } from './models/mock.model';
import { MOCK_REGISTRY } from './mock.registry';

@Injectable({ providedIn: 'root' })
export class MockConfigService {
  private readonly STORAGE_KEY = 'b2c-mock-dev-config';

  private readonly _state = signal<MockActiveState>(this.loadFromStorage());

  readonly state = this._state.asReadonly();

  readonly isMockActive = computed(() => Object.values(this._state()).some((v) => v !== null));

  getActiveScenario(endpointKey: string): string | null {
    return this._state()[endpointKey] ?? null;
  }

  setScenario(endpointKey: string, scenarioKey: string): void {
    this._state.update((current) => ({ ...current, [endpointKey]: scenarioKey }));
    this.persist();
  }

  clearEndpoint(endpointKey: string): void {
    this._state.update((current) => ({ ...current, [endpointKey]: null }));
    this.persist();
  }

  clearAll(): void {
    this._state.set(this.buildInitialState());
    this.persist();
  }

  private loadFromStorage(): MockActiveState {
    const initial = this.buildInitialState();
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        return { ...initial, ...(JSON.parse(saved) as MockActiveState) };
      }
    } catch {
      // localStorage unavailable or data malformed — use defaults
    }
    return initial;
  }

  private buildInitialState(): MockActiveState {
    return Object.fromEntries(Object.keys(MOCK_REGISTRY).map((key) => [key, null]));
  }

  private persist(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this._state()));
    } catch {
      // localStorage unavailable — state lives in memory only
    }
  }
}
